import { Tooltip } from 'antd';
import { DataNode } from 'antd/es/tree';
import { i18n } from 'translations';

import { ActivityExtendedFragment } from 'generated/graphql';

interface ActivityChildren extends ActivityExtendedFragment {
  // Maybe undefined, the last child will not have the property
  children?: (ActivityExtendedFragment & ActivityChildren)[];
}

const isParentActivity = (activity: ActivityChildren) =>
  !!activity.children?.length;

const isHiddenActivity = (activity: ActivityChildren) => activity.hidden;

const activityHasLinkedData = (
  activity: ActivityChildren,
  hiddenActivitiesWithTasks: string[],
) => hiddenActivitiesWithTasks.includes(activity.id);

const hiddenActivityTreeNodeTitle = (title: string) => (
  <Tooltip title={i18n.t('tasksAnalyzer.activityHiddenWithData')}>
    <span style={{ opacity: 0.6 }}>{title}</span>
  </Tooltip>
);

export const buildCheckableActivityTree = (
  activities: ActivityChildren[],
  hiddenActivitiesWithTask: string[],
): DataNode[] => {
  return activities.reduce<DataNode[]>((acc, cur) => {
    if (isParentActivity(cur)) {
      const { children, ...parentActivityData } = cur;

      return [
        ...acc,
        {
          ...parentActivityData,
          // Children verified in isParentActivity()
          children: buildCheckableActivityTree(
            children!,
            hiddenActivitiesWithTask,
          ),
        },
      ];
    }

    // "Normal" activity - Nothing to do here
    if (!isHiddenActivity(cur)) {
      return [...acc, cur];
    }

    const hasLinkedData = activityHasLinkedData(cur, hiddenActivitiesWithTask);

    // Hidden activity with no linked data is discarded from activity tree
    if (!hasLinkedData) {
      return acc;
    }

    // Hidden activity with linked data is displayed but with a info tooltip
    if (hasLinkedData) {
      return [
        ...acc,
        {
          ...cur,
          title: hiddenActivityTreeNodeTitle(cur.title),
        },
      ];
    }

    return acc;
  }, []);
};

function getActivityChildrenIds(children: ActivityChildren[]): string[] {
  return children.reduce((acc: string[], child) => {
    if (child.children?.length) {
      return [...acc, ...getActivityChildrenIds(child.children)];
    }
    return [...acc, child.id];
  }, []);
}

export function checkAllActivities(activities?: ActivityChildren[]) {
  return (
    activities?.reduce<string[]>((acc, activity) => {
      if (activity.children?.length) {
        return [
          ...acc,
          ...getActivityChildrenIds(activity.children as ActivityChildren[]),
        ];
      }
      return [...acc, activity.id];
    }, []) ?? []
  );
}
